import type { Op } from "../../content/modes";

export const TIERS = [1, 2, 3, 4, 5] as const;
export type Tier = (typeof TIERS)[number];

export type Sym = "+" | "−" | "×" | "÷";
/** The gap the child fills in. It can sit anywhere in the question. */
export const SLOT = "?";
export type Token = number | Sym | "=" | typeof SLOT;

export const OP_SYM: Record<Op, Sym> = { add: "+", sub: "−", mul: "×", div: "÷" };

export interface Question {
  typeId: string;
  op: Op;
  tier: Tier;
  points: number;
  /** e.g. [7, "+", SLOT, "=", 12] */
  tokens: Token[];
  /** Always a non-negative integer of at most three digits. */
  answer: number;
  /** The underlying fact, e.g. "5+7", used to track and replay missed facts. */
  factKey: string;
}

const WORD: Record<Sym | "=", string> = {
  "+": "plus",
  "−": "take away",
  "×": "times",
  "÷": "divided by",
  "=": "equals",
};

/** Apply one operation. Division is exact by construction. */
export function apply(sym: Sym, a: number, b: number): number {
  switch (sym) {
    case "+":
      return a + b;
    case "−":
      return a - b;
    case "×":
      return a * b;
    case "÷":
      return a / b;
  }
}

/** "7 + ? = 12" style text, with the slot shown as `blank`. */
export function questionText(tokens: Token[], blank = "?"): string {
  return tokens.map((t) => (t === SLOT ? blank : String(t))).join(" ");
}

/** The full question with the answer written into the slot, for the correction line. */
export function solvedText(q: Question): string {
  return questionText(
    q.tokens.map((t) => (t === SLOT ? q.answer : t)),
    String(q.answer),
  );
}

/** What read-aloud says: "What is 7 plus 5?" or "7 plus what equals 12?" */
export function spokenText(tokens: Token[]): string {
  const words = (ts: Token[]) =>
    ts.map((t) => (t === SLOT ? "what" : typeof t === "number" ? String(t) : WORD[t])).join(" ");
  const last = tokens.length - 1;
  if (tokens[last] === SLOT && tokens[last - 1] === "=")
    return `What is ${words(tokens.slice(0, last - 1))}?`;
  return `${words(tokens)}?`;
}

/**
 * Key for the fact behind a question. Order doesn't matter for + and ×, so
 * "7+5" and "5+7" are the same fact. Two-step facts keep their order.
 */
export function factKey(a: number, sym: Sym, b: number): string {
  if ((sym === "+" || sym === "×") && b < a) return `${b}${sym}${a}`;
  return `${a}${sym}${b}`;
}

const FACT_RE = /^(\d+)(?:([+−×÷])(\d+))+$/;

/** Parse a fact key back into numbers and operators, or null if it isn't one. */
export function parseFact(key: string): { nums: number[]; syms: Sym[] } | null {
  if (!FACT_RE.test(key)) return null;
  const nums = key.split(/[+−×÷]/).map(Number);
  const syms = (key.match(/[+−×÷]/g) ?? []) as Sym[];
  return { nums, syms };
}

/**
 * Rebuild a plain "a op b = ?" question from a fact key, so Practice can
 * replay a missed fact. Returns null for a key that doesn't parse or doesn't
 * give a whole, non-negative, three-digit answer.
 */
export function questionFromFact(
  key: string,
  meta: { typeId: string; op: Op; tier: Tier; points: number },
): Question | null {
  const f = parseFact(key);
  if (!f) return null;
  let answer = f.nums[0]!;
  const tokens: Token[] = [answer];
  f.syms.forEach((sym, i) => {
    const n = f.nums[i + 1]!;
    answer = apply(sym, answer, n);
    tokens.push(sym, n);
  });
  if (!Number.isInteger(answer) || answer < 0 || answer > 999) return null;
  tokens.push("=", SLOT);
  return { ...meta, tokens, answer, factKey: key };
}

/** Same question on screen (used to avoid an immediate repeat). */
export function sameQuestion(x: Question | null, y: Question): boolean {
  return x !== null && x.typeId === y.typeId && questionText(x.tokens) === questionText(y.tokens);
}
